import React, { useEffect, useId, useRef, useState } from "react"
import ReactCountryFlag from "react-country-flag"
import * as styles from "./language-selector.module.scss"
import * as config from "../../../../../../config"
import { arrowBackIcon } from "../../../../../../static/images"

import brFlag from "../../../../../../static/images/brFlag.png"
import luaFlag from "../../../../../../static/images/luaFlag.png"
import { detectWebView } from "../../../../../utils/detectWebView"

export type RelativeLanguagePaths = {
  locale: string
  value: string
}[]

type RenderProps = {
  locale: string
  relativeLanguagePaths?: RelativeLanguagePaths
  openUpward?: boolean
}

const LanguageSelector: React.FC<RenderProps> = ({ locale, relativeLanguagePaths, openUpward = false }) => {
  const [isOpen, setIsOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const firstMenuItemRef = useRef<HTMLAnchorElement>(null)
  const focusFirstItemOnOpen = useRef(false)
  const generatedId = useId().replace(/:/g, "")
  const buttonId = `language-selector-button-${generatedId}`
  const menuId = `language-selector-list-${generatedId}`

  const mainLocale = config.locales[0].toLowerCase().split("-").reverse().join("-")
  const otherLocales = config.locales
    .map(otherLocale => otherLocale.toLowerCase().split("-").reverse().join("-"))
    .filter(locales => locales !== locale)
  const menuLocales = [locale, ...otherLocales]

  const closeMenu = () => {
    focusFirstItemOnOpen.current = false
    setIsOpen(false)
  }
  const hasDropdown = otherLocales.length > 0

  useEffect(() => {
    if (isOpen && focusFirstItemOnOpen.current) {
      focusFirstItemOnOpen.current = false
      firstMenuItemRef.current?.focus()
    }
  }, [isOpen])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault()
      closeMenu()
      menuButtonRef.current?.focus()
      return
    }
    if (event.target === menuButtonRef.current && event.key === "ArrowDown") {
      event.preventDefault()
      focusFirstItemOnOpen.current = true
      setIsOpen(true)
    }
  }

  const getLocaleLink = (currentLocale: string) => {
    const relativeLocaleLink = relativeLanguagePaths?.find(relativeLink => relativeLink.locale === currentLocale)
    if (currentLocale === mainLocale) {
      return `/${relativeLocaleLink ? relativeLocaleLink.value : ""}`
    }
    return `/${currentLocale}/${relativeLocaleLink ? relativeLocaleLink.value : ""}`
  }

  const trigram = process.env.GATSBY_SUBSIDIARY_TRIGRAM
  const WEBVIEW_FLAG_WIDTH = "229px"
  const WEBVIEW_FLAG_HEIGHT = "51px"

  const getLocaleFlag = (localeFlag: string) => {
    switch (localeFlag) {
      case "en":
      case "gb-en":
        return "gb"
      case "us-en":
        return "us"
      case "eu-en":
        return "eu"
      case "sn-fr":
        return "sn"
      default:
        return localeFlag
    }
  }
  const renderBrFlag = () => {
    // Manually rendering the brittany flag to bypass the react-country-flag that doesn't support the brittany flag
    // Only the MOR (Morlaix) subsidiary uses the brittany flag
    return (
      <img
        src={brFlag}
        alt=""
        className={styles.countryFlag}
        style={{
          display: "inline-block",
          verticalAlign: "middle",
          width: isWebView ? WEBVIEW_FLAG_WIDTH : "29px",
          height: isWebView ? WEBVIEW_FLAG_HEIGHT : "17px",
        }}
      />
    )
  }

  const renderLuaFlag = () => {
    // Manually rendering the brittany flag to bypass the react-country-flag that doesn't support the brittany flag
    // Only the LUAS SUB subsidiary uses the brittany flag
    return (
      <img
        src={luaFlag}
        alt=""
        className={styles.countryFlag}
        style={{
          display: "inline-block",
          verticalAlign: "middle",
          width: "29px",
          height: "17px",
        }}
      />
    )
  }

  const renderCountryFlag = (localeFlag: string) => {
    if (trigram === "MOR" && getLocaleFlag(localeFlag) === "br") {
      return renderBrFlag()
    }

    //Or set directly trigram === LUA ???
    if ((trigram === "LUA" && localeFlag === "en") || (trigram === "LUA" && localeFlag === "ga")) {
      return renderLuaFlag()
    }

    return (
      <ReactCountryFlag
        className={styles.countryFlag}
        countryCode={getLocaleFlag(localeFlag)}
        style={{
          width: isWebView ? WEBVIEW_FLAG_WIDTH : "29px",
          height: isWebView ? WEBVIEW_FLAG_HEIGHT : "21px",
        }}
        alt=""
        svg
      />
    )
  }

  const languageCode = locale.split("-").find(part => config["language_selector_title"][part]) || config.locale
  const languageSelectorLabel = config["language_selector_title"][languageCode]
  const baseMenuLabel = languageSelectorLabel || "Change language"
  const isWebView = detectWebView()
  const menuAriaLabel = isWebView ? baseMenuLabel : `${locale.toUpperCase()} – ${baseMenuLabel}`

  if (!hasDropdown) {
    // Single language with no switch possible: hide the flag entirely in webview only
    if (isWebView) {
      return null
    }
    return (
      <div className={styles.languageSelectorMenu}>
        <button type="button" id={buttonId} className={styles.menuButton} disabled aria-label={menuAriaLabel}>
          {renderCountryFlag(locale)}
          <span className={styles.countryLabel} lang={languageCode}>
            {locale}
          </span>
        </button>
      </div>
    )
  }

  return (
    <div
      className={`${styles.languageSelectorMenu} ${openUpward ? styles.openUpward : ""}`}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          closeMenu()
        }
      }}
    >
      <button
        id={buttonId}
        type="button"
        className={styles.menuButton}
        aria-controls={menuId}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(open => !open)}
        onKeyDown={handleKeyDown}
        aria-label={menuAriaLabel}
        ref={menuButtonRef}
      >
        {renderCountryFlag(locale)}
        {!isWebView && (
          <span className={styles.countryLabel} lang={languageCode}>
            {locale}
          </span>
        )}
        {!isWebView && <img src={arrowBackIcon} className={styles.arrowIcon} alt="" aria-hidden="true" />}
      </button>
      <div className={isOpen ? `${styles.dropdownContent} ${styles.active}` : styles.dropdownContent} hidden={!isOpen}>
        <div id={menuId} role="listbox" aria-labelledby={buttonId} hidden={!isOpen}>
          {menuLocales.map((otherLocale, index) => {
            const otherLanguageCode =
              otherLocale.split("-").find(part => config["language_selector_title"][part]) || otherLocale
            const otherLanguageTag = otherLanguageCode
            return (
              <a
                key={index}
                ref={index === 0 ? firstMenuItemRef : undefined}
                className={styles.subMenuButton}
                href={getLocaleLink(otherLocale)}
                hrefLang={otherLanguageCode}
                role="option"
                aria-selected={otherLocale === locale}
                aria-current={otherLocale === locale ? "page" : undefined}
                onKeyDown={handleKeyDown}
              >
                {renderCountryFlag(otherLocale)}
                {!isWebView && (
                  <span className={styles.countryLabel} lang={otherLanguageTag}>
                    {otherLocale}
                  </span>
                )}
              </a>
            )
          })}
        </div>
      </div>
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {isOpen ? `Current language: ${locale}` : ""}
      </p>
    </div>
  )
}

export default LanguageSelector
